// Tracking-snippet shapes and defaults. Client-safe (no server imports) so the
// /admin/settings form can import it; the DB read/write lives in lib/trackingDb.
//
// Every third-party tag the site renders is configured here, so the ids can be
// changed in the admin panel rather than in code. They used to be NEXT_PUBLIC_*
// environment variables, which are baked into the bundle at BUILD time — so
// changing one meant editing .env.production on the server and rebuilding, and
// an id that was never set looked identical to a tag that was broken. The env
// vars are still read, but only as the default when the admin has nothing
// saved, so an existing deployment keeps behaving exactly as it did.

/** Apollo.io website tracker — the account this site was set up with. */
export const APOLLO_DEFAULT_APP_ID = "6a01e56ac49ec10011bf0a43";

/** Apollo.io inbound form enrichment. A DIFFERENT app id from the tracker
 *  above — Apollo issues one per product, and they are not interchangeable. */
export const APOLLO_FORMS_DEFAULT_APP_ID = "6a72e5072d0e330018852b8e";

/** HubSpot visitor tracking — the portal this site was set up with. */
export const HUBSPOT_DEFAULT_PORTAL_ID = "247083540";

/** HubSpot serves the loader from a host tied to the portal's DATA REGION,
 *  not from one global host: this account is na2 (js-na2.hs-scripts.com), and
 *  an EU or na1 portal is served from a different prefix. So the portal id
 *  alone does not identify the script — pasting an id from a portal in another
 *  region into the admin panel would request a file that does not exist, and
 *  the tag would simply never load. If that ever happens, this constant is the
 *  thing to change. */
export const HUBSPOT_JS_HOST = "js-na2.hs-scripts.com";

/** The loader's src. https rather than the protocol-relative "//" HubSpot
 *  hands out — the site is https-only so the two resolve identically here, and
 *  an explicit scheme is one less thing to be wrong if it is copied elsewhere. */
export const hubspotSrc = (portalId: string) =>
  `https://${HUBSPOT_JS_HOST}/${encodeURIComponent(portalId)}.js`;

/**
 * The one host whose traffic belongs in the analytics accounts.
 *
 * Local development runs against the production content database, so a laptop
 * serving localhost:3000 is, as far as every tag is concerned, the live site:
 * a page opened while building a feature became a visitor in HubSpot, a
 * recorded session in Clarity, a company in Apollo. nginx sends www to the
 * apex, so visitors are only ever on this one.
 */
export const TRACKING_HOST = "tablespace.com";

/**
 * Wrap a snippet so it does nothing off the live site.
 *
 * The test has to live INSIDE the snippet rather than deciding whether to
 * render it, because these are inline scripts in the server-rendered HTML:
 * they have already executed by the time anything on the client could tell
 * which host it is on. Rendering them conditionally would also mean rendering
 * them differently on the server and the client, and the one that matters —
 * the GTM loader — only runs at all because it is in that initial HTML.
 */
export const onlyOnTrackingHost = (js: string) =>
  `if(location.hostname===${JSON.stringify(TRACKING_HOST)}){${js}}`;

/** True only in a browser on the live site. For a tag that next/script injects
 *  after hydration, where the snippet wrapper above has nothing to wrap. */
export const isTrackingHost = () =>
  typeof window !== "undefined" && window.location.hostname === TRACKING_HOST;

/** Google Tag Manager container. */
export const GTM_DEFAULT_ID = "GTM-KKPPNTN9";

export type TrackingSettings = {
  /** Google Tag Manager container id (GTM-XXXXXXX). */
  gtmId: string;
  gtmEnabled: boolean;
  /** GA4 Measurement ID (G-XXXXXXXXXX) loaded directly, bypassing GTM.
   *  Usually empty: GA is normally wired inside the GTM container instead. */
  ga4Id: string;
  ga4Enabled: boolean;
  /** Microsoft Clarity project id. */
  clarityId: string;
  clarityEnabled: boolean;
  /** Apollo.io app id; the tracker is skipped when this is empty. */
  apolloAppId: string;
  apolloEnabled: boolean;
  /** Apollo.io form-enrichment app id; skipped when empty. */
  apolloFormsAppId: string;
  apolloFormsEnabled: boolean;
  /** HubSpot portal id; visitor tracking is skipped when this is empty. */
  hubspotPortalId: string;
  hubspotEnabled: boolean;
  /** Free-form snippets for anything else (verification tags, pixels…). */
  headHtml: string;
  bodyHtml: string;
};

/** What the site renders when the DB is unreachable: the ids that were in the
 *  environment before any of this was editable, so a DB outage degrades to the
 *  previous behaviour rather than to no analytics at all. */
export const DEFAULT_TRACKING: TrackingSettings = {
  gtmId: process.env.NEXT_PUBLIC_GTM_ID || GTM_DEFAULT_ID,
  gtmEnabled: true,
  ga4Id: process.env.NEXT_PUBLIC_GA_ID || "",
  ga4Enabled: true,
  clarityId: process.env.NEXT_PUBLIC_CLARITY_ID || "",
  clarityEnabled: true,
  apolloAppId: APOLLO_DEFAULT_APP_ID,
  apolloEnabled: true,
  apolloFormsAppId: APOLLO_FORMS_DEFAULT_APP_ID,
  // Off until someone switches it on in /admin/settings — see optedIn() in
  // lib/trackingDb. Held pending the privacy policy wording.
  apolloFormsEnabled: false,
  hubspotPortalId: HUBSPOT_DEFAULT_PORTAL_ID,
  hubspotEnabled: true,
  headHtml: "",
  bodyHtml: "",
};

/**
 * The Apollo loader, verbatim from Apollo's install instructions: it injects
 * their tracker into <head> itself, so where this runs on the page is
 * irrelevant. `appId` is interpolated as a JSON string.
 */
export const apolloSnippet = (appId: string) =>
  `function initApollo(){var n=Math.random().toString(36).substring(7),o=document.createElement("script");` +
  `o.src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+n,o.async=!0,o.defer=!0,` +
  `o.onload=function(){window.trackingFunctions.onLoad({appId:${JSON.stringify(appId)}})},` +
  `document.head.appendChild(o)}initApollo();`;

/**
 * Apollo.io inbound form enrichment.
 *
 * Unlike the other tags on this page, this one is not passive: before Apollo
 * has loaded it injects a stylesheet that HIDES every form on the page with an
 * email field, showing a spinner in its place, and removes it again when
 * Apollo says it is ready. That is how the enrichment gets to fill fields
 * before the visitor sees them.
 *
 * Two changes from the snippet Apollo supply, both about what happens when
 * their script does not arrive — an ad blocker, a corporate proxy, a bad
 * network. Every enquiry form on this site is behind that stylesheet, so the
 * failure mode is lead forms that cannot be filled in:
 *
 *   - the timeout is 5s, not 15s. Apollo's own snippet logs "timeout after 5s"
 *     while waiting fifteen, so 5s is what they documented and fifteen seconds
 *     of invisible enquiry form is not a trade this site should make;
 *   - the stylesheet is also dropped on pagehide, so a form cannot stay hidden
 *     in a page restored from the back/forward cache.
 *
 * Everything else — the selectors, the spinner, the init call — is Apollo's.
 */
export const apolloFormsSnippet = (appId: string) =>
  `(function initApolloInbound(){` +
  `var TIMEOUT_MS=5000;var timeoutId;` +
  `var style=document.createElement('style');style.id='apollo-form-prehide-css';` +
  `style.textContent='form:has(input[type=\"email\" i]),form:has(input[name=\"Email*\" i]),.hs-form-iframe{position:relative!important}` +
  `form:has(input[type=\"email\" i])::before,form:has(input[name=\"Email*\" i])::before,.hs-form-iframe::before{content:\"\";position:absolute;inset:0;display:flex;align-items:center;justify-content:center;width:50px;height:50px;margin:auto;border:2.5px solid #e1e1e1;border-top:2.5px solid #9ea3a6;border-radius:50%;animation:spin 1s linear infinite;background-color:transparent;pointer-events:auto;z-index:999999;opacity:1}` +
  `form:has(input[type=\"email\" i]) *,form:has(input[name=\"Email*\" i]) *,.hs-form-iframe *{opacity:0!important;user-select:none!important;pointer-events:none!important}` +
  `@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}';` +
  `(document.head||document.documentElement).appendChild(style);` +
  `function cleanup(){var el=document.getElementById('apollo-form-prehide-css');if(el)el.remove();` +
  `if(timeoutId)clearTimeout(timeoutId);window.removeEventListener('pagehide',cleanup);}` +
  `window.addEventListener('pagehide',cleanup);` +
  `timeoutId=setTimeout(function(){console.warn('[Apollo] Form enrichment timed out - revealing forms.');cleanup();},TIMEOUT_MS);` +
  `var nocache=Math.random().toString(36).substring(7);` +
  `var script=document.createElement('script');` +
  `script.src='https://assets.apollo.io/js/apollo-inbound.js?nocache='+nocache;script.defer=true;` +
  `script.onerror=function(){console.error('[Apollo] Failed to load form enrichment script');cleanup();};` +
  `script.onload=function(){try{window.ApolloInbound.formEnrichment.init({appId:${JSON.stringify(appId)},` +
  `onReady:function(){cleanup();},` +
  `onError:function(err){console.error('[Apollo] Form enrichment init error:',err);cleanup();}});}` +
  `catch(err){console.error('[Apollo] Error initializing form enrichment:',err);cleanup();}};` +
  `document.head.appendChild(script);})();`;

/** Microsoft Clarity, verbatim from Clarity's install screen. */
export const claritySnippet = (id: string) =>
  `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};` +
  `t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;` +
  `y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})` +
  `(window, document, "clarity", "script", ${JSON.stringify(id)});`;

/** The GTM loader, verbatim from Google's install instructions. */
export const gtmSnippet = (id: string) =>
  `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer',${JSON.stringify(id)});`;
